import { useMemo } from 'react';

import {
	type AvailabilityContext,
	isFieldAvailable
} from '@/core/dcr/fields/availability';
import { useUnavailableFields } from '@/core/dcr/hooks/useUnavailableFields';
import { getPicklistOptions, type Picklists } from '@/core/dcr/picklists';
import type { EntityType } from '@/core/graphql/generated/types';
import { usePermissionCheck } from '@/core/store/auth/authSelectors';
import { useGetSuggestedDcrFieldsQuery } from '@/features/DcrForm/_graphql/query/GetSuggestedDcrFields.hook';
import {
	buildSuggestionInput,
	getSuggestionCandidates
} from '@/features/DcrForm/_hooks/useSuggestedFields';

type Props = {
	entityType: EntityType;
	countryId: string | undefined;
	picklists: Picklists;
	picklistsLoading: boolean;
};

export const usePrefetchSuggestedFields = ({
	entityType,
	countryId,
	picklists,
	picklistsLoading
}: Props) => {
	const hasPermission = usePermissionCheck();
	const { unavailableFieldIds, loading: unavailableLoading } =
		useUnavailableFields({
			enabled: !!countryId,
			entityType,
			countryId
		});

	const input = useMemo(() => {
		const availability: AvailabilityContext = {
			countryId,
			unavailable: unavailableFieldIds,
			hasPermission,
			hasOptions: source => getPicklistOptions(picklists, source).length > 0
		};
		const candidates = getSuggestionCandidates(entityType, (id, def) =>
			isFieldAvailable(id, def, availability)
		);

		return buildSuggestionInput(entityType, candidates);
	}, [countryId, entityType, hasPermission, picklists, unavailableFieldIds]);

	useGetSuggestedDcrFieldsQuery({
		skip: !countryId || picklistsLoading || unavailableLoading,
		variables: { input }
	});
};
